import {
    MOBILE_RETENTION_STORES,
    MobileOfferCatalogEntry,
    MobileOfferProvider,
    MobileRetentionPlanOffers,
    MobileRetentionStripeCoupon,
    MobileRetentionStripePlan,
} from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CreditCard, Megaphone, Plus, RefreshCw } from 'lucide-react'
import { mobileOfferError, mobileOffersApi } from './api'
import { CodesChip, OfferButton, offerFieldClass, OfferNotice, PlanStatus, planStyle, storeMark } from './ui'
import { usePollWhile } from './usePollWhile'
import { offerDescription, offerLinkClass, planLabels, storePlatformLabels } from './offerLabels'

const NO_OFFER = ''
const months = (count: number) => `${count} ${count === 1 ? 'month' : 'months'}`
const termsLabel = (offer: MobileOfferCatalogEntry) =>
    `${offer.terms.percentOff}% off · ${months(offer.terms.discountedPeriods)}${offer.terms.environment === 'sandbox' ? ' (test purchases)' : ''}`
const couponLabel = (coupon: MobileRetentionStripeCoupon) =>
    `${coupon.percentOff}% off · ${months(coupon.durationMonths)}${coupon.name ? ` — ${coupon.name}` : ''}`
/**
 * While an App Store code batch imports, the tab re-checks with backoff (5s, 10s, 20s, 40s, then every
 * 60s) and gives up after 10 minutes — Apple can take up to an hour, and a stuck batch must not keep an
 * open tab polling forever. The Refresh button still works after that.
 */
const CODES_POLL = { firstMs: 5_000, maxMs: 60_000, giveUpMs: 10 * 60_000 }

/**
 * Fill fields an older main-service may not send yet (Gluon and main-service deploy independently):
 * the tab degrades to "no offer / no codes" instead of crashing on a missing map.
 */
function withDefaults(plan: Partial<MobileRetentionPlanOffers> & Pick<MobileRetentionPlanOffers, 'plan'>): MobileRetentionPlanOffers {
    return {
        plan: plan.plan,
        inUse: { apple: plan.inUse?.apple ?? null, google: plan.inUse?.google ?? null },
        offers: plan.offers ?? [],
        appleCodesAvailable: plan.appleCodesAvailable ?? {},
        deliverable: plan.deliverable ?? {},
        appleCodesPending: plan.appleCodesPending ?? {},
    }
}

/** Each plan shows one cancellation-retention offer per store: iOS and Android are chosen separately. */
export function RetentionOffersPage() {
    const [plans, setPlans] = useState<MobileRetentionPlanOffers[]>([])
    const [stripePlans, setStripePlans] = useState<MobileRetentionStripePlan[]>([])
    const [busy, setBusy] = useState(true)
    const [switching, setSwitching] = useState<string | null>(null)
    const [generating, setGenerating] = useState<string | null>(null)
    // An App Store offer picked for iOS that has no codes yet: it can't be put in use until it has
    // codes, so the row asks to generate them (or pick another) instead of refusing the pick.
    const [needsCodes, setNeedsCodes] = useState<{ plan: MobilePlan; offerId: string } | null>(null)
    const [error, setError] = useState('')
    const sequence = useRef(0)
    const load = useCallback(async (quiet = false) => {
        const request = ++sequence.current
        if (!quiet) setBusy(true)
        setError('')
        try {
            // The card row reads Stripe (live API calls). A quiet poll only watches App Store code batches
            // import, so it skips Stripe — the Card row can't change then. A Stripe hiccup must not hide the store rows.
            const [result, stripe] = await Promise.all([
                mobileOffersApi.retention(),
                quiet
                    ? Promise.resolve(undefined)
                    : mobileOffersApi.stripeRetention().catch((failure: unknown) => {
                          // Keep the store rows; say WHY the Card row is missing (403 vs 500 vs network) and leave a trace.
                          console.warn('[RetentionOffers] Stripe coupons for the Card row failed to load', failure)
                          return { failed: mobileOfferError(failure) }
                      }),
            ])
            if (request === sequence.current) {
                setPlans(result.map(withDefaults))
                if (stripe && 'failed' in stripe) {
                    // Don't leave a previous load's picks on screen under a "could not load" banner — operators would act on them.
                    setStripePlans([])
                    setError(`Could not load Stripe coupons for the Card row: ${stripe.failed}`)
                } else if (stripe) setStripePlans(stripe)
            }
        } catch (failure) {
            if (request === sequence.current) setError(mobileOfferError(failure))
        } finally {
            if (request === sequence.current) setBusy(false)
        }
    }, [])
    useEffect(() => {
        void load()
        return () => {
            sequence.current += 1
        }
    }, [load])
    // While a code batch is still importing, refresh quietly until it lands.
    const codesPending = plans.some((plan) => Object.values(plan.appleCodesPending).some(Boolean))
    usePollWhile(codesPending, () => void load(true), CODES_POLL)
    const generateCodes = async (offerId: string) => {
        setGenerating(offerId)
        setError('')
        try {
            await mobileOffersApi.generateRetentionCodes(offerId)
            await load()
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            setGenerating(null)
        }
    }
    const choose = async (plan: MobilePlan, store: MobileOfferProvider, offerId: string, appleCodes: number) => {
        setError('')
        if (store === MobileOfferProvider.APPLE && offerId !== NO_OFFER && appleCodes === 0) {
            setNeedsCodes({ plan, offerId })
            return
        }
        if (store === MobileOfferProvider.APPLE) setNeedsCodes(null)
        setSwitching(`${plan}:${store}`)
        try {
            await (offerId === NO_OFFER ? mobileOffersApi.clearRetention(plan, store) : mobileOffersApi.activateRetention(offerId, store))
            await load()
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            setSwitching(null)
        }
    }
    const chooseStripe = async (plan: MobilePlan, couponId: string) => {
        setError('')
        setSwitching(`${plan}:stripe`)
        try {
            await mobileOffersApi.setStripeRetention(plan, couponId === NO_OFFER ? null : couponId)
            await load()
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            setSwitching(null)
        }
    }
    // Generate the picked offer's first batch, then put it in use for iOS (activation needs codes to exist).
    const generateAndUse = async (offerId: string) => {
        setGenerating(offerId)
        setError('')
        try {
            await mobileOffersApi.generateRetentionCodes(offerId)
            await mobileOffersApi.activateRetention(offerId, MobileOfferProvider.APPLE)
            setNeedsCodes(null)
            await load()
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            setGenerating(null)
        }
    }
    return (
        <div className="space-y-4">
            <section className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                    <Megaphone size={20} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold">Retention offers</h2>
                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                        These discounts are shown to subscribers when they try to cancel. Each plan uses one offer per channel — iOS, Android and card (Stripe)
                        are chosen separately.
                    </p>
                </div>
                <OfferButton variant="ghost" aria-label="Refresh retention offers" disabled={busy} onClick={() => void load()}>
                    <RefreshCw size={18} />
                </OfferButton>
            </section>
            {error && <OfferNotice error>{error}</OfferNotice>}
            {busy && !plans.length ? (
                <div className="space-y-3" role="status" aria-label="Loading retention offers">
                    {[1, 2, 3].map((key) => (
                        <div key={key} className="h-40 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
                    ))}
                </div>
            ) : (
                plans.map(({ plan, inUse, offers, appleCodesAvailable, appleCodesPending, deliverable }) => {
                    const style = planStyle[plan]
                    const byStore = {
                        apple: offers.find((offer) => offer.id === inUse.apple),
                        google: offers.find((offer) => offer.id === inUse.google),
                    }
                    // Which offers each store could ever show — the server's deliverable map (one rule, not re-derived here).
                    const storeOffers = {
                        apple: offers.filter((offer) => deliverable[offer.id]?.apple),
                        google: offers.filter((offer) => deliverable[offer.id]?.google),
                    }
                    const picked = needsCodes?.plan === plan ? offers.find((offer) => offer.id === needsCodes.offerId) : undefined
                    const stripe = stripePlans.find((row) => row.plan === plan)
                    return (
                        <section
                            key={plan}
                            aria-label={`${planLabels[plan]} retention offer`}
                            className={`rounded-xl border bg-white shadow-sm dark:bg-slate-900 ${style.card}`}
                        >
                            <header className="flex flex-wrap items-center gap-4 p-5">
                                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${style.tile}`}>
                                    <style.Icon size={22} aria-hidden="true" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="text-lg font-semibold">{planLabels[plan]}</h3>
                                        <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${style.badgeClass}`}>{style.badge}</span>
                                    </div>
                                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{style.tagline}</p>
                                </div>
                                <PlanStatus ios={!!byStore.apple} android={!!byStore.google} card={!!stripe?.couponId} />
                                <Link
                                    to={`/billing/mobile-offers/new?purpose=retention&plan=${plan}`}
                                    className={`inline-flex min-h-10 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-blue-600 ${style.create}`}
                                >
                                    <Plus size={16} />
                                    Create offer
                                </Link>
                            </header>
                            {!offers.length ? (
                                <p className="border-t border-slate-100 px-5 py-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                                    No retention offers for this plan yet — create one to show cancelling subscribers a discount.
                                </p>
                            ) : (
                                MOBILE_RETENTION_STORES.map((store) => {
                                    const current = byStore[store]
                                    const Mark = storeMark[store]
                                    // Keep the current pick listed even if it can't serve this store (put in use before per-store choice).
                                    const options = current && !storeOffers[store].includes(current) ? [current, ...storeOffers[store]] : storeOffers[store]
                                    const selected = store === MobileOfferProvider.APPLE && picked ? picked.id : (current?.id ?? NO_OFFER)
                                    const codes = current ? (appleCodesAvailable[current.id] ?? 0) : 0
                                    return (
                                        <div key={store} className="border-t border-slate-100 px-5 py-3 dark:border-slate-800">
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                                <div className="flex w-44 shrink-0 items-center gap-3">
                                                    <Mark />
                                                    <div>
                                                        <p className="text-sm font-semibold">{storePlatformLabels[store]}</p>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">Offer shown on cancel</p>
                                                    </div>
                                                </div>
                                                <select
                                                    aria-label={`${planLabels[plan]} ${storePlatformLabels[store]} offer shown on cancel`}
                                                    className={`${offerFieldClass} w-full sm:w-72`}
                                                    value={selected}
                                                    disabled={busy || !!switching || !!generating}
                                                    onChange={(event) =>
                                                        void choose(plan, store, event.target.value, appleCodesAvailable[event.target.value] ?? 0)
                                                    }
                                                >
                                                    <option value={NO_OFFER}>No offer (go straight to cancellation)</option>
                                                    {options.map((offer) => (
                                                        <option key={offer.id} value={offer.id}>
                                                            {store === MobileOfferProvider.APPLE && !appleCodesAvailable[offer.id]
                                                                ? `${termsLabel(offer)} — ${appleCodesPending[offer.id] ? 'generating codes' : 'no codes yet'}`
                                                                : termsLabel(offer)}
                                                        </option>
                                                    ))}
                                                </select>
                                                {/* iOS: the code pool is always visible and can always be topped up — not only when low. */}
                                                {store === MobileOfferProvider.APPLE && current && !picked && (
                                                    <div className="flex items-center gap-2">
                                                        <CodesChip codes={codes} pending={!!appleCodesPending[current.id]} />
                                                        <button
                                                            type="button"
                                                            className="whitespace-nowrap text-xs font-semibold text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
                                                            disabled={!!generating}
                                                            onClick={() => void generateCodes(current.id)}
                                                        >
                                                            {generating === current.id ? 'Generating…' : 'Generate codes'}
                                                        </button>
                                                    </div>
                                                )}
                                                <p className="min-w-0 flex-1 text-sm text-slate-500 dark:text-slate-400">
                                                    {switching === `${plan}:${store}`
                                                        ? 'Switching…'
                                                        : current
                                                          ? `${storePlatformLabels[store]} subscribers are offered ${offerDescription(current.terms)}.`
                                                          : storeOffers[store].length
                                                            ? `No offer — ${storePlatformLabels[store]} subscribers go straight to cancellation.`
                                                            : store === MobileOfferProvider.APPLE
                                                              ? 'No offer created in the App Store yet.'
                                                              : 'No offer created in Google Play yet.'}
                                                </p>
                                                {current && (
                                                    <Link
                                                        to={`/billing/mobile-offers/offers/${current.id}`}
                                                        className={offerLinkClass}
                                                        aria-label={`${planLabels[plan]} ${storePlatformLabels[store]} offer details`}
                                                    >
                                                        Details
                                                        <ArrowRight size={16} />
                                                    </Link>
                                                )}
                                            </div>
                                            {store === MobileOfferProvider.APPLE && current && !picked && codes === 0 && (
                                                <p role="alert" className="mt-2 text-sm font-medium text-red-700 dark:text-red-400">
                                                    iOS subscribers get no offer until codes exist — generate a batch (Apple can take up to an hour to activate
                                                    new codes).
                                                </p>
                                            )}
                                            {store === MobileOfferProvider.APPLE && picked && (
                                                <div className="mt-2 flex flex-wrap items-center gap-3">
                                                    <p role="alert" className="text-sm font-medium text-amber-700 dark:text-amber-400">
                                                        {`${termsLabel(picked)} has no App Store codes yet. Generate codes to use it for iOS, or choose another offer.`}
                                                    </p>
                                                    <OfferButton disabled={!!generating} onClick={() => void generateAndUse(picked.id)}>
                                                        {generating === picked.id ? 'Generating…' : 'Generate codes & use'}
                                                    </OfferButton>
                                                    <OfferButton variant="ghost" disabled={!!generating} onClick={() => setNeedsCodes(null)}>
                                                        Cancel
                                                    </OfferButton>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })
                            )}
                            {stripe && (
                                <StripeRetentionRow
                                    plan={plan}
                                    row={stripe}
                                    switching={switching === `${plan}:stripe`}
                                    disabled={busy || !!switching || !!generating}
                                    onChoose={(couponId) => void chooseStripe(plan, couponId)}
                                />
                            )}
                        </section>
                    )
                })
            )}
        </div>
    )
}

/**
 * Card (Stripe) subscribers: a coupon from Gluon's Promo Codes page (valid, percent off for a fixed
 * number of months, made for this plan). Claiming attaches it to their subscription from the next invoice.
 */
function StripeRetentionRow({
    plan,
    row,
    switching,
    disabled,
    onChoose,
}: {
    plan: MobilePlan
    row: MobileRetentionStripePlan
    switching: boolean
    disabled: boolean
    onChoose: (couponId: string) => void
}) {
    const current = row.coupons.find((coupon) => coupon.id === row.couponId)
    return (
        <div className="border-t border-slate-100 px-5 py-3 dark:border-slate-800">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex w-44 shrink-0 items-center gap-3">
                    <CreditCard size={20} className="text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                    <div>
                        <p className="text-sm font-semibold">Card (Stripe)</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Coupon shown on cancel</p>
                    </div>
                </div>
                <select
                    aria-label={`${planLabels[plan]} card coupon shown on cancel`}
                    className={`${offerFieldClass} w-full sm:w-72`}
                    value={row.couponId ?? NO_OFFER}
                    disabled={disabled}
                    onChange={(event) => onChoose(event.target.value)}
                >
                    <option value={NO_OFFER}>No offer (go straight to cancellation)</option>
                    {/* A picked coupon that stopped qualifying (expired, edited) stays visible so the pick is never silent. */}
                    {row.couponId && !current && <option value={row.couponId}>{`${row.couponId} — no longer usable`}</option>}
                    {row.coupons.map((coupon) => (
                        <option key={coupon.id} value={coupon.id}>
                            {couponLabel(coupon)}
                        </option>
                    ))}
                </select>
                <p className="min-w-0 flex-1 text-sm text-slate-500 dark:text-slate-400">
                    {switching
                        ? 'Switching…'
                        : current
                          ? `Card subscribers are offered ${current.percentOff}% off for ${months(current.durationMonths)}, from their next invoice.`
                          : row.couponId
                            ? 'The picked coupon is no longer usable — card subscribers go straight to cancellation.'
                            : row.coupons.length
                              ? 'No offer — card subscribers go straight to cancellation.'
                              : 'No coupon for this plan yet.'}
                </p>
                <Link to="/promo-codes" className={offerLinkClass} aria-label={`Create a ${planLabels[plan]} coupon on the Promo Codes page`}>
                    Create coupon
                    <ArrowRight size={16} />
                </Link>
            </div>
        </div>
    )
}
